import { AUTOCOMPLETE_CATEGORY_LIMIT, AUTOCOMPLETE_PRODUCT_LIMIT, MINIMAL_SEARCH_QUERY_LENGTH } from 'config/constants';
import { mapSimpleArticlesInterface } from 'connectors/articleInterface/ArticleInterface';
import { mapSimpleCategoryConnectionApiData } from 'connectors/categories/Categories';
import { mapAutocompleteProductConnectionType } from 'connectors/products/Products';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { AutocompleteSearchQueryApi, useAutocompleteSearchQueryApi } from 'graphql/generated';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useMemo } from 'react';
import { useCookiesStore } from 'store/useCookiesStore';
import { AutocompleteSearchType } from 'types/search';

export const useAutocompleteSearch = (autocompleteSearch: string): [AutocompleteSearchType | undefined, boolean] => {
    const userIdentifier = useCookiesStore((store) => store.userIdentifier);

    const [result] = useAutocompleteSearchQueryApi({
        variables: {
            search: autocompleteSearch,
            userIdentifier,
            maxCategoryCount: AUTOCOMPLETE_CATEGORY_LIMIT,
            maxProductCount: AUTOCOMPLETE_PRODUCT_LIMIT,
        },
        pause: autocompleteSearch.length < MINIMAL_SEARCH_QUERY_LENGTH,
        requestPolicy: 'network-only',
    });
    const { currencyCode } = useDomainConfig();

    useQueryError(result.error);

    return useMemo(
        () => [
            autocompleteSearch.length < MINIMAL_SEARCH_QUERY_LENGTH || !result.data
                ? undefined
                : mapSearchResult(result.data, currencyCode),
            result.fetching,
        ],
        [autocompleteSearch.length, currencyCode, result.data, result.fetching],
    );
};

const mapSearchResult = (apiData: AutocompleteSearchQueryApi, currencyCode: string): AutocompleteSearchType => {
    return {
        ...apiData,
        articlesSearch: mapSimpleArticlesInterface(apiData.articlesSearch),
        categoriesSearch: mapSimpleCategoryConnectionApiData(apiData.categoriesSearch),
        productsSearch: mapAutocompleteProductConnectionType(apiData.productsSearch, currencyCode),
    };
};
